import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Input } from 'antd';

import { assignmentsSet, assignmentWindowHide } from 'core/store/modules/common/actions';
import { useShallowEqualSelector } from 'core/modules/hooks';
import type { UserMember } from 'core/types';
import { StatusSelector, UserSelector } from '../../form';
import { Form, Modal, TimeDatePicker } from '../../ui';

import HistoryCollapse from './HistoryCollapse';
import {
  useDisabledAssignmentElements,
  useEditAssignment,
  useInitAssignment,
  useStatusAssignment,
} from './hooks';
import addDate from './utils/add-date';
import addHistory from './utils/add-history';
import initAssignment from './utils/init-assignment';

const { TextArea } = Input;

function AssignmentDialog(): JSX.Element {
  const dispatch = useDispatch();

  const {
    id,
    item: assignment,
    root,
    statusGet,
    statusSet,
    title,
    users,
  } = useShallowEqualSelector(
    ({
      assignment: { item, statusGet, statusSet },
      editor: {
        document: { title },
        id,
      },
      organization: { users },
      workspace: { id: root },
    }) => ({
      users,
      id,
      item,
      statusGet,
      statusSet,
      title,
      root,
    }),
  );

  const [editAssignment, setEditAssignment] = useState<any>(initAssignment(id, root));
  const [disabled, setDisabled] = useState<boolean>(false);

  useInitAssignment({ dispatch, id, root, setEditAssignment });
  useDisabledAssignmentElements({ assignment, editAssignment, setDisabled });
  useStatusAssignment({ dispatch, editAssignment, statusSet });
  useEditAssignment({ assignment, setEditAssignment, id, root, statusGet });

  const onHideHandler = () => {
    dispatch(assignmentWindowHide());
  };

  const mergeAssignment = (currentObject: any) => ({
    ...editAssignment,
    ...currentObject,
  });

  const onSubmitHandler = () => {
    // add history if it needs.
    const history = addHistory(editAssignment, assignment);
    const date = addDate(editAssignment);

    dispatch(assignmentsSet(mergeAssignment({ history, ...date })));
  };

  const userSelectedHandler = ({ displayName: responsibleName, id: responsible }: UserMember) => {
    setEditAssignment(mergeAssignment({ responsible, responsibleName }));
  };

  // eslint-disable-next-line no-shadow
  const statusSelectedHandler = (status: string) => {
    setEditAssignment(mergeAssignment({ status }));
  };

  const dueHandler = (due: string) => {
    setEditAssignment(mergeAssignment({ due }));
  };

  const commentHandler = (comment: string) => {
    setEditAssignment(mergeAssignment({ comment }));
  };

  const [form] = Form.useForm();

  return (
    <Modal
      okButtonProps={{ disabled }}
      onCancel={onHideHandler}
      onOk={onSubmitHandler}
      title={`Assignment - ${title as string}`}
      open
    >
      <Form form={form} layout="vertical">
        <Form.Item htmlFor="responsible" label="Responsible">
          {/* <UserSelector callback={userSelectedHandler} value={editAssignment.responsible} /> */}
        </Form.Item>

        <Form.Item htmlFor="status" label="Status">
          <StatusSelector callback={statusSelectedHandler} value={editAssignment.status} />
        </Form.Item>

        <Form.Item htmlFor="due" label="Due">
          <TimeDatePicker onChange={(value: any) => dueHandler(value)} value={editAssignment.due} />
        </Form.Item>

        <Form.Item htmlFor="comment" label="Comment">
          <TextArea onChange={e => commentHandler(e.target.value)} value={editAssignment.comment} />
        </Form.Item>
      </Form>
      <HistoryCollapse history={editAssignment.history} users={users} />
    </Modal>
  );
}

export { AssignmentDialog };
